<!--
  =====================================================================
  SECTION TRIPLE TITLE - THREE LINE ANIMATED TITLE
  =====================================================================
  
  Displays three lines of text with animated colored underlines.
  
  FEATURES:
  - Random content selection from array prop on mount
  - Three colored bars that expand on hover (primary, secondary, tertiary)
  - Responsive font sizes (clamp)
  - Cascading indentation for visual interest
  - Compact spacing for above-the-fold optimization
  
  =====================================================================
-->

<template>
  <section class="section-triple-title" v-if="selectedContent">
    <div class="title-wrapper">
      <h1 class="line line-1">{{ selectedContent.line1 }}</h1>
      <h1 class="line line-2">{{ selectedContent.line2 }}</h1>
      <h1 class="line line-3">{{ selectedContent.line3 }}</h1>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PROPS
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Array} content - Array of title objects with line1, line2, line3 properties
 */
const props = defineProps({
  content: {
    type: Array,
    required: true,
    validator: (arr) => arr && arr.length > 0
  }
})

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE
// ───────────────────────────────────────────────────────────────────

/** Currently displayed title content (random selection) */
const selectedContent = ref(null)

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE
// ───────────────────────────────────────────────────────────────────

/**
 * Select random title from content array on mount
 */
onMounted(() => {
  const randomIndex = Math.floor(Math.random() * props.content.length)
  selectedContent.value = props.content[randomIndex]
})
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ------------------------------------------------------------------
   Reset body per eliminare padding e margini laterali
------------------------------------------------------------------ */
:global(body) {
  margin: 0;
  padding: 0;
}

/* ------------------------------------------------------------------
   Stili base del componente triple title
------------------------------------------------------------------ */
.section-triple-title {
  padding: $space-4 $space-1 $space-2 $space-1;

  .title-wrapper {
    width: 100%;
    max-width: 2000px;
    margin: 0 auto;
    margin-left: clamp(2rem, 8vw, 12rem);
    display: flex;
    flex-direction: column;
    @include flex-gap($space-2, column);
    position: relative;
    padding: 0 $space-1;
  }

  .line {
    font-size: clamp(2.0rem, calc(0.933rem + 1.667vw), 2.6rem);
    font-weight: 300;
    color: $text-color;
    margin: 0;
    line-height: 1.2;
    position: relative;
    cursor: default;
    transition: all 0.3s ease;
    word-wrap: break-word;
    overflow-wrap: break-word;
    display: inline-block;

    /* ------------------------------------------------------------------
       Barre colorate sotto ogni riga
    ------------------------------------------------------------------ */
    // Barra sottostante per riga 1 - metà inferiore del testo
    &.line-1::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -100px;
      width: 20%;
      height: 20px;
      background-color: rgba($clr-primary, 0.7);
      transition: width 0.8s ease;
    }

    // Barra sottostante per riga 2 - metà inferiore del testo
    &.line-2::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -100px;
      width: 20%;
      height: 20px;
      background-color: rgba($clr-secondary, 0.7);
      transition: width 0.8s ease;
    }

    // Barra sottostante per riga 3 - metà inferiore del testo
    &.line-3::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -100px;
      width: 20%;
      height: 20px;
      background-color: rgba($clr-tertiary , 0.7);
      transition: width 0.8s ease;
    }

    /* ------------------------------------------------------------------
       Effetto hover - espansione barre
    ------------------------------------------------------------------ */
    // Hover - la barra si espande fino alla fine del testo (inline-block width)
    @media (hover: hover) {
      &:hover::before {
        width: calc(50% + 100px);
      }
    }
  }

  /* ------------------------------------------------------------------
     Scalatura e posizionamento delle righe
  ------------------------------------------------------------------ */
  // Scalatura delle righe - eliminiamo tutto lo spazio a destra
  .line-1 {
    margin-left: 0;
    margin-right: 0;
  }

  .line-2 {
    margin-left: clamp(60px, 12vw, 120px);
    margin-right: 0;
  }

  .line-3 {
    margin-left: clamp(120px, 24vw, 240px);
    margin-right: 0;
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Display medio-grande (1300px)
  ------------------------------------------------------------------ */
  @media (max-width: 1300px) {
    padding: $space-5 $space-0-5 $space-2 $space-0-5;

    .line-1, .line-2, .line-3 {
      margin-right: 0;
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Tablet (1024px)
  ------------------------------------------------------------------ */
  @media (max-width: 1024px) {
    padding: $space-4 $space-0-5 $space-1-5 $space-0-5;

    .line {
      font-size: clamp(1.4rem, 4vw, 2.0rem);

      &.line-1::before,
      &.line-2::before,
      &.line-3::before {
        height: 16px;
        left: -80px;
      }

      @media (hover: hover) {
        &:hover::before {
          width: calc(100% + 80px - 32px);
        }
      }
    }

    .line-1, .line-2, .line-3 {
      margin-right: 0;
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Tablet piccolo (768px)
  ------------------------------------------------------------------ */
  @media (max-width: 768px) {
    padding: $space-5 $space-0-5 $space-1 $space-0-5;

    .title-wrapper {
      margin-left: clamp(1rem, 5vw, 6rem);
    }

    .line {
      font-size: clamp(1.2rem, 4.5vw, 2rem);

      &.line-1::before,
      &.line-2::before,
      &.line-3::before {
        height: 13px;
        left: -60px;
      }

      @media (hover: hover) {
        &:hover::before {
          width: calc(100% + 60px - 32px);
        }
      }
    }

    .line-1, .line-2, .line-3 {
      margin-right: 0;
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile (480px)
  ------------------------------------------------------------------ */
  @media (max-width: 480px) {
    padding: $space-3 $space-0-5 $space-0-25 $space-0-5;

    .title-wrapper {
      @include flex-gap($space-1, column);
      padding: 0 $space-0-5;
    }

    .line {
      font-size: clamp(1rem, 5.5vw, 1.6rem);

      &.line-1::before,
      &.line-2::before,
      &.line-3::before {
        height: 10px;
        left: -40px;
      }

      @media (hover: hover) {
        &:hover::before {
          width: calc(100% + 40px - 32px);
        }
      }
    }

    .line-1 {
      margin-right: 0;
    }

    .line-2 {
      margin-left: 30px;
      margin-right: 0;
    }

    .line-3 {
      margin-left: 60px;
      margin-right: 0;
    }
  }

  /* ------------------------------------------------------------------
     Media queries responsive - Mobile molto piccolo (360px)
  ------------------------------------------------------------------ */
  @media (max-width: 360px) {
    padding: $space-2 $space-0-5 $space-0-25 $space-0-5;

    .line {
      font-size: clamp(0.9rem, 6vw, 1.4rem);

      &.line-1::before,
      &.line-2::before,
      &.line-3::before {
        height: 8px;
        left: -30px;
      }

      @media (hover: hover) {
        &:hover::before {
          width: calc(100% + 30px - 32px);
        }
      }
    }

    .line-1, .line-2, .line-3 {
      margin-right: 0;
    }
  }
}
</style>